import type { Surface } from "../level";
import type { Skater } from "../skater";
import { spinDegrees } from "../tricks";
import { airGaps, farRamp } from "./air";
import { bail } from "./bail";
import { revert } from "./ground";
import { startManual } from "./manual";
import { sideLanded } from "./side";
import type { Landing, SkaterEvent } from "./state";
import {
  FAKIE_OLLIE_POINTS,
  GAP_TRANSFER_POINTS,
  LAND_ANGLE,
  LAND_LINK_WINDOW,
  MAX_SPEED,
  NOLLIE_POINTS,
  PERFECT_ANGLE,
  REVERT_WINDOW,
  SLOPPY_ANGLE,
} from "./tuning";

export function land(sk: Skater, s: Surface, events: SkaterEvent[]): void {
  const air = sk.air!;
  // A transfer air coming down a far ramp's transition, assisted or not, is
  // judged against its line like a vert landing (#77).
  const q = s.quarter;
  if (air.transfer && !air.vertAxis && q && farRamp(air, q) && sk.vx * q.axisX + sk.vz * q.axisZ < 0) {
    air.vertAxis = { x: q.axisX, z: q.axisZ };
    air.transfer.ramp = true;
  }
  sk.setNormal(s);
  const n = sk.normal;
  const vn = sk.vx * n.x + sk.vy * n.y + sk.vz * n.z;
  const tx = sk.vx - vn * n.x;
  const ty = sk.vy - vn * n.y;
  const tz = sk.vz - vn * n.z;
  const f = sk.forward;
  const fn = f.x * n.x + f.z * n.z;
  let bx = f.x - fn * n.x;
  let by = -fn * n.y;
  let bz = f.z - fn * n.z;
  const bl = Math.hypot(bx, by, bz) || 1;
  bx /= bl;
  by /= bl;
  bz /= bl;
  const along = tx * bx + ty * by + tz * bz;
  const tmag = Math.hypot(tx, ty, tz);

  // The landing stat widens (or narrows) every landing window.
  const widen = sk.scale("landing");
  const landCos = Math.cos(LAND_ANGLE * widen);
  // Mid-flip, or not back on the board from a special or round from a body
  // flip (#138): either way you bail.
  const midFlip = air.flip !== null || air.grab?.special === true || !!air.grab?.timed;
  let cos = tmag > 1.5 ? Math.abs(along) / tmag : null;
  let speed = along;
  if (air.vertAxis && s.quarter) {
    // Near the top of a quarter pipe the wall is almost vertical, so projecting
    // the board onto it blows a few degrees of heading up into a "sideways"
    // landing. Judge vert landings by heading against the ramp's line instead.
    const a = air.vertAxis;
    const fa = f.x * a.x + f.z * a.z;
    cos = Math.abs(fa);
    if (cos >= landCos) {
      const axisYaw = Math.atan2(a.x, a.z);
      sk.yaw = fa >= 0 ? axisYaw : axisYaw + Math.PI;
      // Nose up the wall means riding down fakie. An acid drop keeps the speed it went in with.
      speed = -Math.sign(fa) * Math.max(tmag, air.acidCarry);
    }
  }
  sk.y = s.h;
  if (midFlip || (cos !== null && cos < landCos)) {
    bail(sk, events);
    return;
  }
  const landing = judgeLanding(sk, cos === null ? null : Math.acos(Math.min(1, cos)));

  sk.mode = "ground";
  const top = MAX_SPEED * sk.special.boost * sk.scale("speed");
  sk.speed = Math.max(-top, Math.min(top, speed));
  creditSpin(sk);
  // Across from one quarter pipe and down another (#77), or over to the one beside it (#116).
  if ((air.transfer?.ramp || sideLanded(air, sk.x, sk.z)) && s.quarter) sk.addTrick("Transfer", GAP_TRANSFER_POINTS);
  sk.landing = landing;
  airGaps(sk, "air");
  sk.air = null;
  // R2 pressed just before touching down on a ramp reverts, and a manual
  // buffered too follows it: air, revert, manual.
  if (s.quarter && sk.revertBuffer > 0) {
    revert(sk);
    if (sk.manualBuffer > 0) startManual(sk);
    return;
  }
  // A manual pressed just before touchdown links the air into it.
  if (sk.manualBuffer > 0) {
    startManual(sk);
    return;
  }
  // Touching down on a ramp opens the revert window; on flat the combo waits a
  // moment for a manual pressed just after touchdown, then lands.
  if (s.quarter) sk.revertWindow = REVERT_WINDOW;
  else sk.linkWindow = LAND_LINK_WINDOW;
}

/**
 * Credits this air's rotation and pop to its first trick ("Nollie FS 360
 * Kickflip"), or scores the air on its own ("BS 180 Ollie", "Nollie",
 * "Fakie Ollie"). Anything popped rolling fakie is named "Fakie ...".
 */
export function creditSpin(sk: Skater): void {
  const air = sk.air!;
  const deg = spinDegrees(air.spin);
  const spun = deg >= 180;
  const fakie = air.fakie;
  if (spun || air.nollie || fakie) {
    const prefix = [air.nollie ? "Nollie" : fakie ? "Fakie" : "", spun ? `${spinSide(sk)} ${deg}` : ""]
      .filter(Boolean)
      .join(" ");
    const spinPoints = spun ? (deg / 180) * 100 : 0;
    if (air.firstIndex >= 0) {
      const e = sk.combo.entries[air.firstIndex]!;
      // "Switch" stays in front: "Switch Nollie 360 Kickflip".
      const name = e.name.startsWith("Switch ")
        ? `Switch ${prefix} ${e.name.slice("Switch ".length)}`
        : `${prefix} ${e.name}`;
      sk.combo.rename(air.firstIndex, name);
      const popBonus = air.nollie ? NOLLIE_POINTS / 2 : fakie ? FAKIE_OLLIE_POINTS / 2 : 0;
      sk.combo.addPoints(air.firstIndex, spinPoints + popBonus);
    } else if (air.nollie) {
      sk.addTrick(prefix, spinPoints + NOLLIE_POINTS);
    } else {
      sk.addTrick(`${prefix} Ollie`, spinPoints + (fakie ? FAKIE_OLLIE_POINTS : 0));
    }
  }
  air.spin = 0;
  air.firstIndex = -1;
  air.nollie = false;
  air.fakie = false;
}

/**
 * Frontside or backside for this air's spin. Regular stance (left foot
 * forward) faces the right of the nose, so counterclockwise from above
 * (yaw increasing) turns the chest into the spin first: frontside. Switch
 * has the other foot forward, which swaps them.
 */
export function spinSide(sk: Skater): "FS" | "BS" {
  const air = sk.air!;
  return air.spin > 0 !== sk.switchStance ? "FS" : "BS";
}

/**
 * Judges a landing `off` radians from straight (null when there's no line
 * to judge against, like dropping almost straight down): Sloppy past
 * SLOPPY_ANGLE, Perfect for a spin put down within PERFECT_ANGLE (both
 * scaled by the landing stat). A vert air (#183) is Perfect put down that
 * straight with any trick in it, spin or not, but judged by how far off
 * you were before the vert assist turned you: the assist can save you a
 * Sloppy, never hand you a Perfect.
 */
export function judgeLanding(sk: Skater, off: number | null): Landing | null {
  const air = sk.air!;
  if (off === null) return null;
  const widen = sk.scale("landing");
  if (off > SLOPPY_ANGLE * widen) return "sloppy";
  const vert = !!air.vertAxis;
  const earned = spinDegrees(air.spin) >= 180 || (vert && air.firstIndex >= 0);
  if (earned && off + (vert ? Math.max(0, air.vertAssist - air.vertCarve) : 0) <= PERFECT_ANGLE * widen) return "perfect";
  return null;
}
